import { Link } from "react-router-dom";
import { Home, Building2, ArrowRight, Phone } from "lucide-react";
import { SERVICES } from "@/data/services";
import { BUSINESS } from "@/data/businessInfo";
import { SEO } from "@/components/SEO";
import { LocationCardsSection } from "@/components/SharedSections";
import { motion } from "framer-motion";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };

export default function ServicesPage() {
  return (
    <div>
      <SEO title="Electrical Services Port St. Lucie FL - Residential & Commercial | Southern Light Solutions" description="Full-service electrical contractor in Port St. Lucie, FL. EV charger installation, generator installation, panel upgrades, lighting, ceiling fans, code corrections, and troubleshooting. Residential and commercial. Free estimates." path="/services" />
      <section className="bg-[#FAFAFA] py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#BF8D30] mb-3 block" style={{ fontFamily: 'Outfit, sans-serif' }}>Our Services</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#401303] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Electrical Services for Every Need</h1>
            <p className="mt-4 text-base lg:text-lg text-[#6B3A1F] max-w-2xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>From residential repairs to commercial installations, Southern Light Solutions delivers reliable electrical services across the Treasure Coast.</p>
          </div>

          {/* Residential */}
          <div className="mb-16">
            <div className="flex items-center gap-2 mb-6 pb-3 border-b-2 border-[#BF8D30]">
              <Home className="w-5 h-5 text-[#BF8D30]" />
              <h2 className="text-xl font-bold text-[#401303] uppercase tracking-[0.1em]" style={{ fontFamily: 'Outfit, sans-serif' }}>Residential Services</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {SERVICES.map((s, i) => (
                <motion.div key={s.residential.slug} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.3, delay: i * 0.05 }}>
                  <Link to={`/services/${s.residential.slug}`} className="block group h-full" data-testid={`residential-${s.id}`}>
                    <div className="rounded-2xl overflow-hidden shadow-lg group-hover:shadow-2xl group-hover:-translate-y-2 transition-all duration-300 mb-3">
                      <img src={s.image} alt={`Residential ${s.title} in Port St. Lucie`} className="w-full aspect-[4/3] object-cover" />
                    </div>
                    <h3 className="text-base font-bold text-[#401303] mb-1 group-hover:text-[#BF8D30] transition-colors duration-200" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h3>
                      <p className="text-xs text-[#6B3A1F] line-clamp-2 leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.description}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Commercial */}
          <div>
            <div className="flex items-center gap-2 mb-6 pb-3 border-b-2 border-[#317E8C]">
              <Building2 className="w-5 h-5 text-[#317E8C]" />
              <h2 className="text-xl font-bold text-[#401303] uppercase tracking-[0.1em]" style={{ fontFamily: 'Outfit, sans-serif' }}>Commercial Services</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {SERVICES.map((s, i) => (
                <motion.div key={s.commercial.slug} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.3, delay: i * 0.05 }}>
                  <Link to={`/services/${s.commercial.slug}`} className="block group h-full" data-testid={`commercial-${s.id}`}>
                    <div className="rounded-2xl overflow-hidden shadow-lg group-hover:shadow-2xl group-hover:-translate-y-2 transition-all duration-300 mb-3 relative">
                      <img src={s.image} alt={`Commercial ${s.title} on the Treasure Coast`} className="w-full aspect-[4/3] object-cover" />
                      <div className="absolute top-2 right-2 px-2 py-1 bg-[#317E8C] text-white text-xs font-bold rounded-full" style={{ fontFamily: 'Outfit, sans-serif' }}>Commercial</div>
                    </div>
                    <h3 className="text-base font-bold text-[#401303] mb-1 group-hover:text-[#317E8C] transition-colors duration-200" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h3>
                    <p className="text-xs text-[#6B3A1F] line-clamp-2 leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.description}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-warm-gradient py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Not Sure What You Need?</h2>
          <p className="text-white/80 mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>Call us and describe the issue. We'll help you figure out the best solution.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/book" className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-[#401303] font-bold rounded-full text-sm" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="services-book-btn">
              Book Service <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={`tel:${BUSINESS.phoneRaw}`} className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-white text-white font-bold rounded-full text-sm" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="services-call-btn">
              <Phone className="w-4 h-4" /> {BUSINESS.phone}
            </a>
          </div>
        </div>
      </section>

      <LocationCardsSection title="Areas We Serve" />
    </div>
  );
}
